<template>
  <div class="wm">
    <div v-if="!columns.length" class="dempty">
      Ни одно слово не проходит порог «мин. сессий со словом».
    </div>
    <div v-else class="wm-scroll" ref="scroll" @scroll="onScrollMatrix">
      <!-- Один слушатель на всю сетку вместо трёх на каждую ячейку: при сотне
           слов и десятке блоков это два обработчика против трёх тысяч. -->
      <div class="wm-grid" ref="grid" :style="gridStyle" :class="{ 'with-vmed': geom }"
           @mousemove="onGridMove" @mouseleave="onGridLeave">
        <div class="wm-corner wm-c1"></div>
        <div class="wm-corner wm-c2">медиана</div>
        <!-- Липкими делаем только левые колонки. Прилепить строку заголовков
             к верху экрана нельзя: контейнер с overflow-x:auto превращает
             overflow-y в auto, и sticky top цепляется к нему, а не к странице. -->
        <template v-for="(w, wj) in columns" :key="'h' + w.key">
          <!-- Шапка сегмента — обёртка: кнопки булавки, правки и удаления нельзя вложить
               в кнопку шапки (вложенные кнопки — невалидная разметка). -->
          <div v-if="w.seg" class="ins-segh-wrap" :class="mkClass" :data-idx="wj">
            <span class="ins-seg-tools">
              <!-- Булавка путём, а не символом из шрифта: юникодная в разных системах
                   выглядит по-разному и лезет цветной картинкой. -->
              <button type="button" class="ins-seg-b ins-seg-pin" :class="{ 'is-on': w.pin }"
                      data-help="сегмент/булавка"
                      :title="w.pin ? 'сейчас: закреплён справа' : 'сейчас: в общей сортировке'"
                      :aria-label="w.pin ? 'открепить' : 'закрепить справа'"
                      @click="$emit('seg-pin', w.id)">
                <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true"><path fill="currentColor" d="M9.6 1.2 14.8 6.4l-1.1 1.1-1.2-.3-2.6 2.6.5 2.2c.1.4 0 .8-.3 1.1l-.5.5-3.6-3.6-3.4 3.4-.7-.7 3.4-3.4L1.7 5.7l.5-.5c.3-.3.7-.4 1.1-.3l2.2.5 2.6-2.6-.3-1.2 1.1-1.1z"/></svg>
              </button>
              <button type="button" class="ins-seg-b" data-help="сегмент/правка" aria-label="правка"
                      @click="$emit('seg-edit', w.id)">✎</button>
              <button type="button" class="ins-seg-b" data-help="сегмент/удалить" aria-label="убрать"
                      @click="$emit('seg-del', w.id)">×</button>
            </span>
            <button type="button" class="wm-hcell ins-segh" :class="headClass(w)"
                    :data-help="layers ? 'матрица/шапка слои' : 'матрица/шапка'" :data-help-label="w.label"
                    :title="headTitle(w)"
                    @click="$emit('select-word', w.key)"
            >{{ segShown(w.label) }}<template v-for="part in headParts(w)" :key="part">·<b class="num">{{ part }}</b></template></button>
          </div>
          <button
            v-else
            type="button"
            class="wm-hcell"
            :class="headClass(w)"
            :data-idx="wj"
            :data-help="layers ? 'матрица/шапка слои' : 'матрица/шапка'" :data-help-label="w.label"
            :title="headTitle(w)"
            @click="$emit('select-word', w.key)"
          >{{ w.label }}<template v-for="part in headParts(w)" :key="part">·<b class="num">{{ part }}</b></template></button>
        </template>

        <template v-for="(block, bi) in blocks" :key="'r' + bi">
          <button
            type="button"
            class="wm-rname wm-c1"
            :class="{ 'is-active': sort.key === bi }"
            data-help="матрица/блок" :data-help-label="block"
            :data-bi="bi"
            @click="$emit('sort-block', bi)"
          >
            <span class="wm-arr">{{ sort.key === bi ? (sort.dir === 'desc' ? '▼' : '▲') : '' }}</span>
            <span class="wm-rname-text">{{ block }}</span>
          </button>
          <div class="wm-med wm-c2 num">{{ fmtShare(medians[bi]) }}</div>
          <div
            v-for="(w, wj) in columns" :key="'c' + w.key + '-' + bi"
            class="wm-cell num"
            :class="cellClass(w, bi)"
            :style="cellStyle(w, bi)"
            :data-idx="wj"
            :data-bi="bi"
            :aria-label="cellAria(w, bi)"
            @click="onCellClick(w, bi)"
          ><span>{{ cellText(w, bi) }}</span></div>
        </template>

        <!-- Подвал «итог». Число не удаляется, а прячется под кнопкой A/B: оно задаёт
             ширину ячейки, и без него подвал схлопнулся бы. -->
        <div class="ins-sep"></div>
        <button
          type="button"
          class="wm-rname wm-c1 ins-agg-name"
          :class="{ 'is-active': sort.key === 'total' }"
          data-help="матрица/итог" data-help-label="Итог"
          @click="$emit('sort-total')"
        >
          <span class="wm-arr">{{ sort.key === 'total' ? (sort.dir === 'desc' ? '▼' : '▲') : '' }}</span>
          <span class="wm-rname-text">итог</span>
        </button>
        <div class="wm-med wm-c2 num">{{ fmtShare(medians[blocks.length - 1]) }}</div>
        <div v-for="(w, wj) in columns" :key="'t' + w.key" class="ins-agg num" :data-idx="wj">
          <span class="ins-agg-v">{{ totalText(w) }}</span>
          <!-- Своей подсказки у кнопки нет намеренно: она и так появляется в ответ на
               наведение, а вторая всплывашка закрыла бы соседние столбцы. -->
          <button type="button" class="ins-agg-ab" :aria-label="'A/B тест: ' + w.label + ', итог ' + totalText(w)"
                  @click.stop="$emit('ab', w.key)">A/B</button>
        </div>

        <!-- Ореол сегмента одним слоем на столбец: у клеток свои скругления и зазор,
             и тень на каждой клетке разваливала бы колонку на отдельные ячейки. -->
        <div v-for="h in halos" :key="'halo' + h.key" class="ins-seg-halo" :class="{ 'is-out': segMarks.out }"
             :style="{ left: h.left + 'px', top: h.top + 'px', width: h.w + 'px', height: h.h + 'px' }"></div>

        <template v-if="geom">
          <div class="wm-vmed" :style="{
            left: geom.x + 'px', top: geom.top + 'px', height: (geom.bottom - geom.top) + 'px' }"></div>
          <div class="wm-vmed-pill" :style="{ left: pillLeft + 'px', top: (geom.foot + 8) + 'px' }">
            {{ markerLabel }}
          </div>
          <div v-for="zone in zones" :key="zone.label" class="wm-vmed-zone"
               :style="{ left: zone.left + 'px', top: (geom.foot + 11) + 'px' }">{{ zone.label }}</div>
        </template>
      </div>
    </div>

    <div v-if="columns.length" class="wm-hscroll" ref="proxy" @scroll="onScrollProxy">
      <div :style="{ width: contentWidth + 'px', height: '1px' }"></div>
    </div>

    <div class="wm-tip" ref="tip" style="display:none">
      <div class="wm-tip-word"></div>
      <div class="num wm-tip-val"></div>
      <div class="num wm-tip-sub"></div>
    </div>
  </div>
</template>

<script>
import { bucketIndex, devStep } from '@/utils/wordMatrix';
import { plural } from '@/utils/segments';

export default {
  name: 'WordMatrix',
  props: {
    blocks: { type: Array, required: true },
    // Показанные столбцы К4 в порядке вывода: слова и откреплённые сегменты в общей
    // сортировке, закреплённые сегменты в конце.
    columns: { type: Array, required: true },
    medians: { type: Array, required: true },
    bounds: { type: Array, required: true },
    pick: { type: Function, required: true },
    raw: { type: Function, required: true },
    form: { type: String, required: true },
    showValues: { type: Boolean, required: true },
    showSes: { type: Boolean, default: true },
    showConv: { type: Boolean, default: false },
    sel: { type: Object, default: null },          // null | {key} | {key, bi}
    sort: { type: Object, required: true },
    // Ключи столбцов, попавших в выделение на распределении, или null.
    brushKeys: { type: Object, default: null },
    // Медиана по ключу сортировки, посчитанная по полному набору слов.
    // Считать её здесь нельзя: сюда приходят только видимые столбцы, и
    // порог «мин. сессий со словом» сдвигал бы центр среза.
    sortMedian: { type: Number, default: 0 },
    dev: { type: Boolean, default: false },
    delta: { type: Boolean, default: false },
    iqr: { type: Boolean, default: false },
    marker: { type: Boolean, default: false },
    quarts: { type: Array, default: () => [] },
    cmp: { type: Boolean, default: false },
    cmpKey: { type: String, default: null },
    // Слова, чьи фразы лежат в собираемом сегменте: их шапки жирные.
    hlKeys: { type: Object, default: null },
    segMarks: { type: Object, default: () => ({ lift: true, out: false }) },
    nameMax: { type: Number, default: 8 },
    layers: { type: Boolean, default: false },
  },
  emits: ['select-word', 'select-cell', 'sort-block', 'sort-total', 'ab', 'seg-pin', 'seg-edit', 'seg-del'],
  data() {
    return {
      contentWidth: 0,
      syncing: false,
      observer: null,
      // Геометрия маркера медианы: {x, top, bottom, gridLeft, gridRight} или
      // null. Считается по DOM в measure(), поэтому лежит в data, а не в computed.
      geom: null,
      // Ореолы сегментов: [{key, left, top, w, h}] по клеткам столбца, считаются в measure().
      halos: [],
    };
  },
  computed: {
    // Отметки сегмента. Обе выключены — остаётся серый пунктир по бокам: без него
    // откреплённый сегмент среди слов ничем не отличался бы от данных.
    mkClass() {
      const { lift, out } = this.segMarks;
      return { 'ins-mk-lift': lift, 'ins-mk-out': out, 'ins-mk-plain': !lift && !out };
    },
    markerLabel() {
      return this.keyLabel(this.sort.key) + ' · медиана ' + this.fmtKey(this.sortMedian, this.sort.key);
    },
    // Подписи зон читаются слева направо, поэтому при сортировке по убыванию
    // слева оказывается «выше медианы», при возрастании — наоборот.
    zones() {
      if (!this.geom) return [];
      const desc = this.sort.dir === 'desc';
      const pill = this.pillLeft;
      const out = [];
      const push = (from, to, label) => {
        if (to - from < 70) return;
        const left = from + (to - from) / 2;
        // Рядом с пилюлей подпись не влезет: она шире зазора и наехала бы.
        if (Math.abs(left - pill) <= 110) return;
        out.push({ left: left, label: label });
      };
      push(this.geom.gridLeft, this.geom.x, desc ? 'выше медианы' : 'ниже медианы');
      push(this.geom.x, this.geom.gridRight, desc ? 'ниже медианы' : 'выше медианы');
      return out;
    },
    pillLeft() {
      if (!this.geom) return 0;
      // Пилюля центрирована по линии, но у краёв матрицы её надо подвинуть,
      // иначе половина текста уедет за пределы прокручиваемой области.
      return Math.min(Math.max(this.geom.x, 90), Math.max(90, this.contentWidth - 90));
    },
    gridStyle() {
      // repeat(0, …) — невалидный CSS: браузер отбросил бы всё свойство целиком
      // и раскладка сетки вместе с липкими колонками развалилась бы.
      const cols = this.columns.length
        ? ' repeat(' + this.columns.length + ', minmax(46px, 1fr))'
        : '';
      return { gridTemplateColumns: 'var(--wm-col1) var(--wm-col2)' + cols };
    },
  },
  mounted() {
    this.hoverIdx = null;
    this.measure();
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(this.measure);
      this.observer.observe(this.$refs.grid);
    }
    window.addEventListener('resize', this.measure);
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this.measure);
  },
  updated() {
    // Элементы подвала переиспользуются по ключу: после пересортировки флаг is-on,
    // поставленный вручную, остался бы на столбце, который уехал из-под курсора.
    this.setHoverCol(null);
    this.setHoverCell(null); // и классы ins-hov снять: в прототипе сетка строится заново, подсветки нет
    this.measure();
  },
  methods: {
    measure() {
      const grid = this.$refs.grid;
      if (!grid) return;
      // С контейнера, а не с сетки: у него справа запас под ореол, и полоса-двойник
      // должна докручивать до того же конца.
      const width = this.$refs.scroll.scrollWidth;
      if (width !== this.contentWidth) this.contentWidth = width;
      const geom = this.computeMarker(grid);
      // Сравнение по значению, а не по ссылке: measure() вызывается из
      // updated(), и новый объект на каждом проходе зациклил бы перерисовку.
      if (JSON.stringify(geom) !== JSON.stringify(this.geom)) this.geom = geom;
      const halos = this.computeHalos(grid);
      if (JSON.stringify(halos) !== JSON.stringify(this.halos)) this.halos = halos;
    },
    // Прямоугольник клеток каждого сегмента: слой ореола лежит под клетками, поэтому
    // виден ровно в промежутках вокруг столбца. «Выдвинут» сдвигает клетки — слой едет следом.
    computeHalos(grid) {
      if (!(this.segMarks.lift || this.segMarks.out)) return [];
      const out = [];
      this.columns.forEach((c, j) => {
        if (!c.seg) return;
        const cells = grid.querySelectorAll('.wm-cell[data-idx="' + j + '"]');
        if (!cells.length) return;
        let l = Infinity, t = Infinity, r = -Infinity, b = -Infinity;
        cells.forEach(el => {
          l = Math.min(l, el.offsetLeft); t = Math.min(t, el.offsetTop);
          r = Math.max(r, el.offsetLeft + el.offsetWidth); b = Math.max(b, el.offsetTop + el.offsetHeight);
        });
        const dx = this.segMarks.out ? -4 : 0;
        out.push({ key: c.key, left: l + dx, top: t, w: r - l, h: b - t });
      });
      return out;
    },
    // Геометрия столбца для сцены слоёв (К6.geom): px от левого верхнего угла корня
    // матрицы. Столбец за краем прокрутки сперва подкатывается в видимую область —
    // иначе сцена встала бы за экраном.
    getColumnGeom(key) {
      const j = this.columns.findIndex(c => c.key === key);
      const grid = this.$refs.grid;
      const sc = this.$refs.scroll;
      if (j < 0 || !grid || !sc) return null;
      const cells = Array.from(grid.querySelectorAll('.wm-cell[data-idx="' + j + '"]'));
      const label = grid.querySelector('.wm-rname');
      if (!cells.length || !label) return null;
      const med = grid.querySelector('.wm-med');
      const sticky = label.offsetWidth + (med ? med.offsetWidth : 0) + 4;
      const first = cells[0];
      if (first.offsetLeft < sc.scrollLeft + sticky
          || first.offsetLeft + first.offsetWidth > sc.scrollLeft + sc.clientWidth) {
        sc.scrollLeft = Math.max(0, first.offsetLeft - sticky - 20);
      }
      const base = this.$el.getBoundingClientRect();
      const rs = cells.map(c => c.getBoundingClientRect());
      const lr = label.getBoundingClientRect();
      return {
        colLeft: rs[0].left - base.left,
        colTop: rs[0].top - base.top,
        colW: rs[0].width,
        colH: rs[rs.length - 1].bottom - rs[0].top,
        rows: rs.map(r => ({ top: r.top - base.top, h: r.height })),
        labelLeft: lr.left - base.left,
        labelW: lr.width,
      };
    },
    // Значение ключа сортировки для слова: по нему считается медиана маркера
    // и подпись у слова в шапке.
    keyValue(w, key) {
      if (key === 'ses') return w.ses;
      if (key === 'conv') return w.conv || 0;
      if (key === 'total') return this.pick(w.cells[w.cells.length - 1]);
      return this.pick(w.cells[key]);
    },
    keyLabel(key) {
      if (key === 'ses') return 'сессии';
      if (key === 'conv') return 'конверсии';
      if (key === 'total') return 'итог';
      return this.blocks[key];
    },
    // Сессии и конверсии — штуки, метрика блока и итог — доли. Округление до
    // десятых, а не до целого: медиана чётного набора — это полусумма двух
    // середин, и на целых она врала бы ровно там, где на неё и смотрят.
    // Хвостовой ноль не дописываем: 18 и есть значение с точностью до десятых.
    fmtKey(value, key) {
      const v = Number(value) || 0;
      const round1 = x => Math.round(x * 10) / 10;
      return (key === 'ses' || key === 'conv')
        ? String(round1(v))
        : round1(v * 100) + ' %';
    },
    // Маркер делит слова на «выше медианы» и «ниже» по текущему ключу
    // сортировки. При сортировке по алфавиту и в режиме приростов он
    // бессмыслен, поэтому не рисуется.
    computeMarker(grid) {
      if (!this.marker || this.delta) return null;
      const key = this.sort.key;
      // Закреплённые сегменты стоят справа вне сортировки — маркер их не сканирует.
      const n = this.columns.filter(c => !(c.seg && c.pin)).length;
      if (key === 'alpha' || !n) return null;
      const valueOf = w => this.keyValue(w, key);
      // Медиана приходит снаружи и посчитана по всем словам. Линия при этом
      // ищется среди видимых столбцов — если порог отрезал всё, что ниже
      // медианы, она честно встанет у правого края матрицы.
      const med = this.sortMedian;
      // Сравниваем не сырые значения, а те, что видит пользователь: в ячейке
      // процент округлён до целого, в пилюле медиана — до десятых. На сырых
      // числах ячейка «38» вставала слева от медианы 38.2 %, потому что на
      // самом деле в ней 38.4, и линия выглядела поставленной наугад. Теперь
      // слева от линии ровно те столбцы, чьё показанное число не меньше
      // показанной медианы.
      const counts = key === 'ses' || key === 'conv';
      const shownOf = w => (counts ? valueOf(w) : Math.round(valueOf(w) * 100));
      const shownMed = counts
        ? Math.round(med * 10) / 10
        : Math.round(med * 1000) / 10;
      let k = n;
      for (let i = 0; i < n; i++) {
        const shown = shownOf(this.columns[i]);
        const past = this.sort.dir === 'desc' ? shown < shownMed : shown > shownMed;
        if (past) { k = i; break; }
      }
      // Границу берём по ячейкам, а не по кнопкам-заголовкам: заголовок
      // выровнен по центру колонки (justify-self:center) и заметно уже её,
      // поэтому линия по его краю вставала бы внутрь столбца, а не между
      // столбцами. Ячейки занимают колонку целиком, и середина зазора между
      // соседними ячейками — это и есть граница столбцов.
      const cols = Array.from(grid.querySelectorAll('.wm-cell[data-bi="0"]')).slice(0, n);
      const rows = grid.querySelectorAll('.wm-cell[data-idx="0"]');
      if (!cols.length || !rows.length) return null;
      const first = cols[0];
      const last = cols[cols.length - 1];
      const lastRow = rows[rows.length - 1];
      const half = Math.max(0, (cols.length > 1 ? cols[1].offsetLeft - (first.offsetLeft + first.offsetWidth) : 2) / 2);
      let x;
      if (k === 0) x = first.offsetLeft - half;
      else if (k >= cols.length) x = last.offsetLeft + last.offsetWidth + half;
      else x = cols[k].offsetLeft - half;
      // Пилюля и подписи зон висят под подвалом «итог», а не под последним блоком:
      // иначе они легли бы поверх чисел итога.
      const agg = grid.querySelector('.ins-agg');
      const bottom = lastRow.offsetTop + lastRow.offsetHeight;
      return {
        x: x,
        top: first.offsetTop,
        bottom: bottom,
        foot: agg ? agg.offsetTop + agg.offsetHeight : bottom,
        gridLeft: first.offsetLeft,
        gridRight: last.offsetLeft + last.offsetWidth,
      };
    },
    // Два скроллбара синхронизируются вручную, флаг гасит обратное событие,
    // иначе они зациклятся друг на друге. Флаг снимается в следующем кадре,
    // а не по «эху» события scroll: если запись оказалась no-op (позиция уже
    // совпадала — обычное дело при инерционной прокрутке), эха не будет
    // и флаг залип бы, проглотив следующую прокрутку.
    onScrollMatrix() {
      this.syncTo(this.$refs.proxy, this.$refs.scroll);
    },
    onScrollProxy() {
      this.syncTo(this.$refs.scroll, this.$refs.proxy);
    },
    syncTo(target, source) {
      if (this.syncing || !target || !source) return;
      if (target.scrollLeft === source.scrollLeft) return;
      this.syncing = true;
      target.scrollLeft = source.scrollLeft;
      requestAnimationFrame(() => { this.syncing = false; });
    },
    fmtShare(value) {
      return Math.round((Number(value) || 0) * 100) + ' %';
    },
    // Числа рядом со словом в шапке. Сессии и конверсии показываются ровно по
    // галочкам, в том числе когда по ним же идёт сортировка: галочка — это
    // «что выводить», а не «что не мешает». Значение ключа сортировки по
    // блоку или итогу — отдельная величина, доля, и галочки её не касаются.
    // Подписи предельно короткие («128s», «4c»): заголовки вертикальные,
    // и каждый лишний символ удлиняет шапку матрицы.
    headParts(w) {
      const key = this.sort.key;
      const out = [];
      if (key !== 'alpha' && key !== 'ses' && key !== 'conv') {
        const cell = key === 'total' ? w.cells[w.cells.length - 1] : w.cells[key];
        if (cell) out.push(this.fmtShare(this.pick(cell)));
      }
      if (this.showSes) out.push(w.ses + 's');
      if (this.showConv) out.push((w.conv || 0) + 'c');
      return out;
    },
    headTitle(w) {
      // При слоях клик по шапке слова открывает слои, и строка про профиль спорила бы
      // с плашкой; в прототипе у шапки слова title нет вовсе (3005).
      if (!w.seg) {
        if (this.layers) return null;
        return 'Профиль слова: ' + w.label
          + ' · ' + w.ses + ' ' + plural(w.ses, ['сессия', 'сессии', 'сессий'])
          + ' · ' + (w.conv || 0) + ' ' + plural(w.conv || 0, ['конверсия', 'конверсии', 'конверсий']);
      }
      // Одна сессия несёт одну фразу, поэтому фраза, пришедшая через два слова,
      // считается один раз — об этом и строка про повторы.
      const dups = (w.nParts || 0) - w.phrases.length;
      const nf = w.phrases.length;
      return 'Сегмент «' + w.label + '» — ' + (w.fullName || '') + '.\n'
        + 'Собран из ' + (w.nParts || 0) + ' ' + plural(w.nParts || 0, ['части', 'частей', 'частей'])
        + ', ' + nf + ' ' + plural(nf, ['фраза', 'фразы', 'фраз']) + '. Сессий ' + w.ses
        + (dups > 0
          ? ', ' + dups + ' ' + plural(dups, ['фраза пришла через два слова и учтена',
            'фразы пришли через два слова и учтены', 'фраз пришли через два слова и учтены'])
            + ' один раз.'
          : ', повторов нет.')
        + '\n' + (w.pin ? 'Закреплён справа' : 'Участвует в сортировках наравне со словами') + '.'
        + '\nКлик — ' + (this.layers ? 'раскрыть в слои' : 'профиль') + ', булавка — закрепить, ✎ — правка, × — убрать.';
    },
    // Страховка: шапка вертикальная, и одно длинное имя задрало бы строку заголовков.
    segShown(label) {
      return label.length > this.nameMax ? label.slice(0, this.nameMax - 1) + '…' : label;
    },
    headClass(w) {
      return {
        'is-sel': !this.cmp && this.sel && this.sel.key === w.key,
        'is-pair-a': this.cmp && this.sel && this.sel.bi === undefined && this.sel.key === w.key,
        'is-pair-b': this.cmp && w.key === this.cmpKey,
        'is-brushed': this.inBrush(w),
        'ins-h-hl': !!(this.hlKeys && this.hlKeys.has(w.key)),
      };
    },
    inBrush(w) {
      return !!(this.brushKeys && this.brushKeys.has(w.key));
    },
    totalText(w) {
      const cell = w.cells[w.cells.length - 1];
      if (!cell) return '—';
      return this.form === 'value' ? String(this.raw(cell)) : Math.round(this.pick(cell) * 100) + ' %';
    },
    // Значение ячейки с учётом режима «прирост Δ»: там показывается не сам
    // уровень метрики, а её прирост относительно предыдущего блока.
    shareAt(w, bi) {
      const cur = this.pick(w.cells[bi]);
      if (!this.delta) return cur;
      return cur - (bi > 0 ? this.pick(w.cells[bi - 1]) : 0);
    },
    rawAt(w, bi) {
      const cur = this.raw(w.cells[bi]);
      if (!this.delta) return cur;
      return cur - (bi > 0 ? this.raw(w.cells[bi - 1]) : 0);
    },
    cellText(w, bi) {
      if (!w.cells[bi]) return '—';
      if (this.dev) {
        const diff = Math.round((this.shareAt(w, bi) - (this.medians[bi] || 0)) * 100);
        return diff > 0 ? '+' + diff : String(diff);
      }
      if (this.form === 'value') {
        const v = this.rawAt(w, bi);
        return v === 0 ? '—' : (this.delta && v > 0 ? '+' + v : String(v));
      }
      const p = Math.round(this.shareAt(w, bi) * 100);
      if (p === 0) return '—';
      return this.delta && p > 0 ? '+' + p : String(p);
    },
    cellStyle(w, bi) {
      if (this.dev) {
        const step = devStep(this.shareAt(w, bi) - (this.medians[bi] || 0));
        return { background: 'var(--cell-' + step + ')', color: 'var(--ink-' + step + ')' };
      }
      // bucketIndex сам относит любое неположительное значение к нулевой
      // корзине, поэтому отрицательный прирост клэмпить не нужно.
      const value = this.delta ? this.shareAt(w, bi) : this.pick(w.cells[bi]);
      const step = bucketIndex(value, this.bounds) * 20;
      return { background: 'var(--seq-' + step + ')', color: 'var(--seq-ink-' + step + ')' };
    },
    cellClass(w, bi) {
      const isSel = !this.cmp && this.sel && this.sel.bi === bi && this.sel.key === w.key;
      const pair = this.inPair(w);
      const dimmed = this.cmp
        ? (this.cmpKey !== null || (this.sel && this.sel.bi === undefined)) && !pair
        : (this.sel ? (this.sel.bi === undefined ? this.sel.key !== w.key : !isSel) : false);
      const seg = w.seg ? this.mkClass : {};
      return {
        ...seg,
        'ins-segc': !!w.seg,
        'is-sel': isSel,
        // Выделенный на распределении диапазон гасит остальную матрицу —
        // так найденный там набор слов виден и после переключения вида.
        'is-dim': dimmed || !!(this.brushKeys && !this.inBrush(w)),
        // В паре сравнения цифры видны всегда, независимо от тумблера.
        'show-value': this.showValues || pair,
        'is-outlier': this.iqr && this.isOutlier(w, bi),
        'no-click': this.cmp,
        // Обведены оба столбца пары: среди сотни колонок иначе их не найти.
        'is-pair-a': this.cmp && this.sel && this.sel.bi === undefined && this.sel.key === w.key,
        'is-pair-b': this.cmp && w.key === this.cmpKey,
      };
    },
    isOutlier(w, bi) {
      // Квартили посчитаны по уровню метрики, а в режиме приростов в ячейке
      // показан инкремент. Обводить выброс по уровню поверх цифры прироста
      // значило бы мерить одно, а подсвечивать другое.
      if (this.delta) return false;
      const q = this.quarts[bi];
      if (!q) return false;
      const width = q[1] - q[0];
      const v = this.pick(w.cells[bi]);
      return v < q[0] - 1.5 * width || v > q[1] + 1.5 * width;
    },
    onCellClick(w, bi) {
      // В режиме сравнения ячейки не кликабельны: единственное действие —
      // выбор второго слова в заголовке.
      if (this.cmp) return;
      this.$emit('select-cell', w.key, bi);
    },
    inPair(w) {
      if (!this.cmp) return false;
      const first = this.sel && this.sel.bi === undefined ? this.sel.key : null;
      return w.key === first || w.key === this.cmpKey;
    },
    cellAria(w, bi) {
      const value = this.form === 'value'
        ? this.raw(w.cells[bi]) + ' сессий'
        : this.fmtShare(this.pick(w.cells[bi]));
      return w.label + ', ' + this.blocks[bi] + ': ' + value;
    },
    onGridMove(event) {
      // Кнопка A/B видна только у столбца под курсором. Скриптом, а не :hover: подвал
      // и клетки — соседи в гриде, селектора «сосед с тем же номером столбца» в CSS нет.
      // Через DOM, а не реактивное поле, по той же причине, что и тултип.
      const col = event.target.closest('[data-idx]');
      this.setHoverCol(col ? col.dataset.idx : null);
      const cell = event.target.closest('.wm-cell');
      if (!cell) { this.hideTip(); return; }
      this.setHoverCell(cell);
      const w = this.columns[Number(cell.dataset.idx)];
      if (!w) return;
      this.showTip(event, w, Number(cell.dataset.bi));
    },
    // Тултип обновляется напрямую через DOM, а не через реактивное поле.
    // Шаблон плоский: любое реактивное изменение пересчитывало бы класс,
    // стиль и текст всех ячеек на каждое движение мыши, а при сотнях слов
    // это тысячи пересчётов в секунду плюс принудительный reflow из measure().
    showTip(event, w, bi) {
      const el = this.$refs.tip;
      if (!el) return;
      const p = this.pick(w.cells[bi]);
      const med = this.medians[bi] || 0;
      const diff = Math.round((p - med) * 100);
      el.querySelector('.wm-tip-word').textContent = w.label + ' · ' + this.blocks[bi];
      el.querySelector('.wm-tip-val').textContent =
        this.fmtShare(p) + ' · ' + this.raw(w.cells[bi]) + ' сессий';
      el.querySelector('.wm-tip-sub').textContent =
        'медиана ' + this.fmtShare(med) + ' · ' + (diff > 0 ? '+' + diff : diff) + ' к ней';
      el.style.display = 'block';
      const box = el.getBoundingClientRect();
      el.style.left = Math.min(event.clientX + 14, window.innerWidth - box.width - 8) + 'px';
      el.style.top = Math.min(event.clientY + 16, window.innerHeight - box.height - 8) + 'px';
    },
    onGridLeave() {
      this.setHoverCol(null);
      this.setHoverCell(null);
      this.hideTip();
    },
    // Клетка под курсором подсвечивает свою шапку и подпись блока (прототип 3178).
    // Как и остальное наведение, через DOM: реактивное поле перерисовало бы всю сетку.
    // Прошлая клетка не сбрасывается, пока курсор между клетками, как в прототипе.
    setHoverCell(cell) {
      const grid = this.$refs.grid;
      if (!grid || cell === this.hoverCell) return;
      this.hoverCell = cell;
      grid.querySelectorAll('.ins-hov').forEach(x => x.classList.remove('ins-hov'));
      if (!cell) return;
      const h = grid.querySelector('[data-idx="' + cell.dataset.idx + '"].wm-hcell, '
        + '[data-idx="' + cell.dataset.idx + '"] .wm-hcell');
      const r = grid.querySelector('.wm-rname[data-bi="' + cell.dataset.bi + '"]');
      if (h) h.classList.add('ins-hov');
      if (r) r.classList.add('ins-hov');
    },
    setHoverCol(idx) {
      if (idx === this.hoverIdx) return;
      const grid = this.$refs.grid;
      if (!grid) return;
      const prev = grid.querySelector('.ins-agg.is-on');
      if (prev) prev.classList.remove('is-on');
      this.hoverIdx = idx;
      if (idx === null) return;
      const next = grid.querySelector('.ins-agg[data-idx="' + idx + '"]');
      if (next) next.classList.add('is-on');
    },
    hideTip() {
      const el = this.$refs.tip;
      if (el) el.style.display = 'none';
    },
  },
};
</script>

<style scoped>
.wm { --wm-col1: 150px; --wm-col2: 60px; --wm-gap: 2px; position: relative; }
/* Нативная полоса контейнера скрыта: прокрутку показывает липкая полоса ниже.
   Иначе под матрицей оказывается два скроллбара подряд. Прокрутка колесом,
   тачпадом и с клавиатуры при этом сохраняется. */
.wm-scroll { overflow-x: auto; padding-bottom: 4px; scrollbar-width: none; }
/* Запас справа под ореол закреплённого сегмента: overflow-x режет всё, что за краем
   прокрутки, а тень в прокручиваемую область не входит. В прототипе обёртка без
   overflow, и свечение видно со всех сторон. Ширину полосы прокрутки measure() берёт
   с контейнера, вместе с этим запасом. */
.wm-scroll:has(.ins-seg-halo) { padding-right: 26px; }
.wm-scroll::-webkit-scrollbar { display: none; }
/* position:relative нужен маркеру медианы (Task 9): он позиционируется
   абсолютно внутри сетки и обязан ехать вместе с ней при прокрутке. */
/* min-width: max-content — не косметика, а условие работы липких колонок.
   Липкий элемент не может выйти за пределы своего контейнера, а коробка
   сетки по умолчанию шириной с область прокрутки: содержимое из неё
   вываливается. Колонка держалась ровно до правого края этой коробки —
   на широкой матрице отклеивалась примерно на 1200 px прокрутки, дальше
   уезжала вместе с сеткой. С max-content коробка равна содержимому.
   Ширины колонок при этом те же: узкая матрица по-прежнему растягивается
   на всю область (width:auto), а на широкой колонки и так стоят на своём
   минимуме в 46 px. */
.wm-grid {
  display: grid;
  gap: var(--wm-gap);
  align-items: stretch;
  position: relative;
  min-width: max-content;
}

/* z-index липких колонок обязан быть выше, чем у ячейки под курсором и у
   выбранной ячейки (3), иначе при прокрутке подсвеченная ячейка проезжает
   поверх названия блока и медианы. */
.wm-c1 { position: sticky; left: 0; z-index: 5; background: var(--surface-1); }
.wm-c2 { position: sticky; left: calc(var(--wm-col1) + var(--wm-gap)); z-index: 5; background: var(--surface-1); }

.wm-hcell {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  align-self: end;
  justify-self: center;
  min-height: 150px;
  margin-bottom: 8px;
  white-space: nowrap;
  line-height: 1.1;
  font-size: 12px;
  font-family: inherit;
  color: var(--text-muted);
  border: 0;
  background: none;
  padding: 2px 1px;
  cursor: pointer;
  text-align: start;
}
.wm-hcell:hover { color: var(--text-primary); }
.wm-hcell b { color: var(--text-secondary); font-weight: 650; }
.wm-hcell.is-sel b, .wm-hcell.is-pair-a b { color: var(--accent); }
.wm-hcell.is-pair-b b { color: var(--cmp); }
/* подсветка наведения и набора (v14) раньше выбора и пары: те побеждают, как в П 190–192 */
.wm-hcell.ins-h-hl, .wm-hcell.ins-hov { color: var(--text-primary); font-weight: 650; }
.wm-hcell.is-brushed { color: var(--text-primary); font-weight: 650; }
.wm-hcell.is-sel { color: var(--accent); font-weight: 700; }
.wm-hcell.is-pair-a { color: var(--accent); font-weight: 700; }
.wm-hcell.is-pair-b { color: var(--cmp); font-weight: 700; }

.wm-corner {
  z-index: 6;
  align-self: end;
  font-size: 11.5px;
  color: var(--text-muted);
  background: var(--surface-1);
  padding-bottom: 8px;
}

.wm-rname {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  padding: 0 10px 0 6px;
  font-size: 13px;
  font-family: inherit;
  color: var(--text-secondary);
  border: 0;
  cursor: pointer;
  text-align: right;
  min-width: 0;
}
.wm-rname:hover { color: var(--text-primary); }
.wm-rname.is-active, .wm-rname.ins-hov { color: var(--text-primary); font-weight: 650; }
.wm-rname-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wm-arr { font-size: 9px; width: 9px; color: var(--accent); flex: none; }

.wm-med {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11.5px;
  color: var(--text-muted);
}

.wm-cell {
  border-radius: 4px;
  min-height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11.5px;
  cursor: pointer;
  position: relative;
}
.wm-cell span { opacity: 0; transition: opacity .12s; }
.wm-cell.show-value span { opacity: 1; }
.wm-cell:hover span { opacity: 1; }
.wm-cell:hover { outline: 2px solid var(--text-primary); outline-offset: -1px; z-index: 3; }
.wm-cell.is-dim { opacity: .35; }
.wm-cell.is-dim span { opacity: 0; }
.wm-cell.is-dim:hover span { opacity: 1; }
.wm-cell.is-sel { outline: 2px solid var(--accent); outline-offset: -1px; z-index: 3; }
.wm-cell.is-sel span { opacity: 1; }
.wm-cell.is-outlier { box-shadow: inset 0 0 0 2px var(--text-primary); }
.wm-cell.no-click { cursor: default; }
/* Наведение не должно гасить постоянную обводку столбцов пары —
   иначе рамка мигает ровно на той ячейке, куда смотрит пользователь. */
.wm-cell.no-click:hover:not(.is-pair-a):not(.is-pair-b) { outline: none; }
.wm-cell.is-pair-a { outline: 2px solid var(--accent); outline-offset: -1px; z-index: 3; }
.wm-cell.is-pair-b { outline: 2px solid var(--cmp); outline-offset: -1px; z-index: 3; }

/* Место под пилюлю и подписи зон — они висят ниже последней строки матрицы. */
.wm-grid.with-vmed { padding-bottom: 34px; }
.wm-vmed {
  position: absolute;
  width: 2px;
  /* Линия стоит ровно на границе столбцов, поэтому центрируется по ней сама. */
  transform: translateX(-50%);
  background: var(--text-primary);
  box-shadow: 0 0 0 1.5px var(--surface-1);
  opacity: .85;
  border-radius: 1px;
  pointer-events: none;
  z-index: 4;
}
.wm-vmed-pill {
  position: absolute;
  transform: translateX(-50%);
  font-size: 11px;
  font-weight: 650;
  background: var(--accent);
  color: var(--on-accent);
  border-radius: 999px;
  padding: 3px 10px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 4;
}
.wm-vmed-zone {
  position: absolute;
  transform: translateX(-50%);
  font-size: 11px;
  color: var(--text-muted);
  white-space: nowrap;
  pointer-events: none;
  z-index: 4;
}

/* ---- v14: подвал «итог» с кнопкой A/B ---- */
.ins-sep { grid-column: 1 / -1; height: 8px; border-bottom: 1px solid var(--grid); margin-bottom: 8px; }
.ins-agg-name { font-size: 12px; color: var(--text-muted); min-height: 26px; }
.ins-agg {
  position: relative; font-size: 12px; color: var(--text-secondary); font-weight: 600;
  display: flex; align-items: center; justify-content: center; min-height: 26px;
}
.ins-agg.is-on > .ins-agg-v, .ins-agg:focus-within > .ins-agg-v { visibility: hidden; }
/* Не display:none: кнопка должна оставаться досягаемой табом. Невидима и не ловит
   мышь, пока курсор не над столбцом; с клавиатуры проявляется по фокусу. */
.ins-agg-ab {
  position: absolute; inset: 0; margin: auto; width: max-content; height: 20px;
  display: inline-flex; opacity: 0; pointer-events: none; align-items: center; border: 1px solid var(--accent); background: var(--accent);
  color: var(--on-accent); border-radius: 20px; font-family: inherit; font-size: 10.5px; font-weight: 700;
  letter-spacing: .02em; padding: 0 9px; cursor: pointer; white-space: nowrap;
}
.ins-agg.is-on > .ins-agg-ab, .ins-agg-ab:focus-visible { opacity: 1; pointer-events: auto; }
.ins-agg-ab:hover { filter: brightness(1.12); }

/* ---- v14: шапка слова в собираемом сегменте ---- */

/* ---- v14: столбец сегмента ---- */
.ins-segh-wrap {
  position: relative; align-self: end; justify-self: center;
  display: flex; flex-direction: column; align-items: center;
}
.ins-segh-wrap .wm-hcell { align-self: center; color: var(--text-primary); }
/* второе слово сравнения оранжевое и у сегмента (в П .cmpw с !important) */
.ins-segh-wrap .wm-hcell.is-pair-b { color: var(--cmp); }
/* Кнопки столбиком справа от текста шапки у её нижнего края, снизу вверх булавка,
   ✎, ×, как в прототипе. Абсолютно, чтобы строка заголовков не росла с первым
   сегментом; сбоку, а не поверх, чтобы хвост имени с цифрой сортировки не уходил
   под кнопки. Место есть: столбец не уже 46px, шапка ~16px по центру.
   bottom: 8px — это margin-bottom шапки внутри обёртки. */
.ins-seg-tools {
  position: absolute; left: 100%; bottom: 8px; z-index: 1;
  display: flex; flex-direction: column-reverse; gap: 2px;
}
.ins-seg-b {
  border: 0; background: none; cursor: pointer; font-size: 11px; line-height: 1;
  color: var(--text-muted); padding: 1px 3px; border-radius: 4px; font-family: inherit;
}
.ins-seg-b:hover { color: var(--text-primary); background: rgba(128, 128, 128, .16); }
.ins-seg-pin { display: inline-flex; align-items: center; justify-content: center; transform: rotate(-20deg); }
.ins-seg-pin.is-on { color: var(--accent); transform: none; }
.wm-cell.ins-segc.ins-mk-plain { box-shadow: inset 1px 0 0 var(--text-muted), inset -1px 0 0 var(--text-muted); }
.wm-cell.ins-segc.ins-mk-lift, .wm-cell.ins-segc.ins-mk-out { z-index: 3; }
.ins-segh-wrap.ins-mk-lift .wm-hcell { text-shadow: 0 1px 0 var(--surface-1); }
/* Выдвинут: у шапки сдвиг через left, а не transform — шапка повёрнута на 180°,
   и своя трансформация затёрла бы поворот. */
.wm-cell.ins-segc.ins-mk-out { transform: translateX(-4px); z-index: 4; }
.ins-segh-wrap.ins-mk-out { left: -4px; z-index: 4; }
/* Тени ореола одни на обе темы, как в прототипе: токены --shadow-* в тёмной
   теме гуще, и ореол выходил темнее. */
.ins-seg-halo {
  position: absolute; pointer-events: none; z-index: 2; border-radius: 9px;
  box-shadow: 0 0 0 3px var(--halo-in), 0 0 16px 3px var(--halo), 0 6px 18px -6px rgba(0, 0, 0, .34);
}
.ins-seg-halo.is-out {
  box-shadow: 0 0 0 3px var(--halo-in), 0 0 22px 5px var(--halo),
    12px 0 24px -8px rgba(0, 0, 0, .44), -4px 0 0 0 rgba(0, 0, 0, .10);
}

/* Липкий горизонтальный скролл: полоса остаётся у нижней кромки экрана,
   сколько бы блоков ни было по вертикали. */
.wm-hscroll {
  position: sticky;
  bottom: 0;
  z-index: 5;
  overflow-x: auto;
  overflow-y: hidden;
  height: 14px;
  background: var(--surface-1);
  border-top: 1px solid var(--grid);
}

.wm-tip {
  position: fixed;
  z-index: 50;
  background: var(--tooltip-bg);
  color: var(--tooltip-ink);
  border-radius: 8px;
  padding: 8px 11px;
  font-size: 12.5px;
  pointer-events: none;
  max-width: 260px;
  box-shadow: 0 4px 14px var(--shadow-md);
}
.wm-tip-word { font-weight: 650; margin-bottom: 2px; }
.wm-tip-sub { opacity: .7; }
</style>
